import uuid from 'uuid';
import Fuse from 'fuse.js';
import memoizeOne from 'memoize-one';
import qs from 'query-string';
import { MagicSearchPluginSync, MagicSearchPluginName, ResultItem } from './types';
import { SearchResultsListItemLink } from '../components';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';

const DEFAULT_ICON = ['far', 'folder'];

export type FuseSearchIndexItem = {
  id?: string;
  icon?: FontAwesomeIconProps['icon'];
  title: string;
  description?: string | React.ReactNode;
  path: string;
};

const createFuseSearchIndex = memoizeOne(
  (indexItems: FuseSearchIndexItem[]) =>
    new Fuse<FuseSearchIndexItem>(indexItems, {
      ignoreLocation: true,
      threshold: 0.2,
      includeScore: true,
      includeMatches: true,
      minMatchCharLength: 3,
      shouldSort: true,
      keys: [
        { name: 'path', weight: 1.4 },
        { name: 'title', weight: 1.3 },
        { name: 'description', weight: 0.5 },
      ],
      fieldNormWeight: 1.2,
    }),
);

const fuseSearchPlugin = ({
  headerTitle,
  hint,
  indexItems,
}: {
  headerTitle: string;
  hint: string;
  indexItems: FuseSearchIndexItem[];
}): MagicSearchPluginSync => {
  const fuseSearchIndex = createFuseSearchIndex(indexItems);
  const isMongoId = (searchText: string) => /^[0-9a-fA-F]{24}$/.test(searchText);

  return {
    name: MagicSearchPluginName.FuseSearchPlugin,
    hint: ({ searchScopes }) => (searchScopes.some(scope => scope.id === 'project') ? hint : null),
    onSearch: ({ searchText, searchScopes }) => {
      if (isMongoId(searchText)) return [];
      if (!searchScopes.some(scope => scope.id === 'project')) return [];
      let fuseSearchIndexResults = fuseSearchIndex.search(searchText);
      fuseSearchIndexResults = fuseSearchIndexResults.slice(0, 10);
      fuseSearchIndexResults = fuseSearchIndexResults.filter(result => {
        // lower scores are more relevant, and if there's no score, we just assume it's relevant
        return (result.score ?? 0) < 0.3;
      });

      const results: ResultItem[] = [];

      if (!searchText) {
        return [];
      } else if (!fuseSearchIndexResults.length) {
        return [];
      } else {
        results.push(
          ...fuseSearchIndexResults.map(
            ({ item: { id, title, icon, description, path }, score }) => ({
              id: uuid.v4(),
              Component: SearchResultsListItemLink,
              props: {
                title,
                description,
                icon: icon ?? DEFAULT_ICON,
                to: {
                  pathname: path,
                  // We use the URL because we need something that also works in Anchors open in new tab or window.
                  ...(id
                    ? { search: qs.stringify({ focusElementId: encodeURIComponent(id) }) }
                    : {}),
                },
                type: headerTitle,
              },
              score,
            }),
          ),
        );
      }

      return results;
    },
  };
};

export { fuseSearchPlugin };
